import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { User } from '@supabase/supabase-js';
import { CheckCircle, ExternalLink, CreditCard, Crown, Users, Building2, Zap, X, AlertCircle, Smartphone } from 'lucide-react';
import { Capacitor } from '@capacitor/core';
import { iapService, PRODUCT_IDS } from '../services/iapService';
import { getUserRole } from '../services/teamService';
import { supabase } from '../lib/supabaseClient';

interface SubscriptionStatus {
  isActive: boolean;
  productId?: string;
  expirationDate?: Date;
  purchaseDate?: Date;
  isInTrialPeriod?: boolean;
}

interface IAPProduct {
  id: string;
  title: string;
  description: string;
  price: string;
  currency: string;
}

interface SubscriptionManagementCompactProps {
  currentUser: User | null;
}

// Accurate pricing from pricing.ts
const planDetails = {
  [PRODUCT_IDS.STANDARD]: { 
    name: 'Standard', 
    icon: CreditCard, 
    color: 'blue', 
    price: 59,
    features: ['Floor plans, EPC & inventory tools', 'Retrofit survey workflows', 'View-only templates', '20 AI questions', '20 room scans', 'PDF exports', '50 GB storage'] 
  },
  [PRODUCT_IDS.PRO_TRIAL]: { 
    name: 'Pro Trial', 
    icon: Zap, 
    color: 'purple', 
    price: 0,
    features: ['Trial ends 8th March 2026', 'All Pro features', '20 AI questions', 'Unlimited room scans', '250 GB storage'] 
  },
  [PRODUCT_IDS.PRO]: { 
    name: 'Professional', 
    icon: Crown, 
    color: 'purple', 
    price: 79,
    features: ['Everything in Standard', '200 AI questions', 'Unlimited room scans', 'Editable & reusable templates', 'White-label branding', '250 GB storage'] 
  },
  [PRODUCT_IDS.TEAM]: { 
    name: 'Team', 
    icon: Users, 
    color: 'indigo', 
    price: 299,
    features: ['5 contractors + unlimited admins', 'All Pro features for every individual', 'Job assignment & team dashboards', '1 TB shared storage', 'Additional users: £59/month'] 
  },
  [PRODUCT_IDS.BUSINESS]: { 
    name: 'Business', 
    icon: Building2, 
    color: 'slate', 
    price: 499,
    features: ['10 contractors + unlimited admins', 'All Pro features for every individual', 'Advanced team management', 'Priority support', '3 TB shared storage', 'Additional users: £59/month'] 
  },
};

const SubscriptionManagementCompact: React.FC<SubscriptionManagementCompactProps> = ({ currentUser }) => {
  const [hasAccess, setHasAccess] = useState(false);
  const [checkingAccess, setCheckingAccess] = useState(true);
  const [userRole, setUserRole] = useState<string | null>(null);
  const [currentSubscription, setCurrentSubscription] = useState<SubscriptionStatus | null>(null);
  const [loading, setLoading] = useState(false);
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [products, setProducts] = useState<IAPProduct[]>([]);
  const [purchasing, setPurchasing] = useState<string | null>(null);
  const [isUpdatingPlan, setIsUpdatingPlan] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [loadingProducts, setLoadingProducts] = useState(false);

  useEffect(() => {
    if (currentUser) {
      checkAccess();
    }
  }, [currentUser]);

  useEffect(() => {
    if (hasAccess) {
      loadSubscriptionData();
    }
  }, [hasAccess]);

  // Reload products when modal opens to prevent stuck "Loading..." state
  useEffect(() => {
    if (showPlanModal && hasAccess && Capacitor.isNativePlatform()) {
      console.log('[IAP_COMPACT] Modal opened - refreshing products');
      refreshProducts();
    }
  }, [showPlanModal, hasAccess]);

  const checkAccess = async () => {
    try {
      setCheckingAccess(true);
      // Default to 'personal' if account_type is not set
      const accountType = currentUser?.user_metadata?.account_type || 'personal';
      
      console.log('[IAP_COMPACT] 🔍 Checking access:', { accountType, userId: currentUser?.id });
      
      if (accountType === 'personal') {
        console.log('[IAP_COMPACT] ✅ Personal account - granting access');
        setHasAccess(true);
        setCheckingAccess(false);
        return;
      }
      
      if (accountType === 'business') {
        const role = await getUserRole();
        console.log('[IAP_COMPACT] 🏢 Business account role:', role);
        setUserRole(role);
        setHasAccess(role === 'super_admin');
      } else {
        console.log('[IAP_COMPACT] ⚠️ Unknown account type - denying access');
        setHasAccess(false);
      }
    } catch (err) {
      console.error('[IAP_COMPACT] ❌ Error checking access:', err);
      setHasAccess(false);
    } finally {
      setCheckingAccess(false);
    }
  };

  const refreshProducts = async () => {
    try {
      setLoadingProducts(true);
      console.log('[IAP_COMPACT] Refreshing products...');
      
      await iapService.initialize();
      const availableProducts = await iapService.getProducts();
      setProducts(availableProducts);
      
      console.log('[IAP_COMPACT] Products refreshed:', availableProducts.length);
    } catch (err: any) {
      console.error('[IAP_COMPACT] Failed to refresh products:', err);
    } finally {
      setLoadingProducts(false);
    }
  };

  const loadSubscriptionData = async () => {
    try {
      setLoading(true);
      setError(null);

      // IAP only works on iOS
      if (!Capacitor.isNativePlatform()) {
        console.log('[IAP_COMPACT] Desktop platform - skipping IAP load');
        setLoading(false);
        return;
      }

      await iapService.initialize();
      const availableProducts = await iapService.getProducts();
      setProducts(availableProducts);

      const status = await iapService.getSubscriptionStatus();
      setCurrentSubscription(status);

      console.log('[IAP_COMPACT] Loaded subscription:', { 
        hasActive: status?.isActive, 
        productId: status?.productId,
        expirationDate: status?.expirationDate,
        fullStatus: status 
      });
      
      // Also check user metadata for subscription plan
      const userPlan = currentUser?.user_metadata?.subscription_plan;
      console.log('[IAP_COMPACT] User metadata plan:', userPlan);
      
      // If IAP says inactive but user metadata has a plan, there's a mismatch
      if (!status?.isActive && userPlan) {
        console.warn('[IAP_COMPACT] ⚠️ Mismatch: IAP inactive but user has plan:', userPlan);
      }
    } catch (err: any) {
      console.error('[IAP_COMPACT] Failed to load subscription:', err);
      setError('Failed to load subscription information.');
    } finally {
      setLoading(false);
    }
  };

  const handlePurchase = async (productId: string) => {
    try {
      setPurchasing(productId);
      setError(null);
      setSuccessMessage(null);

      console.log('[IAP_COMPACT] Starting purchase:', productId);

      const result = await iapService.purchase(productId as any);

      if (result.success) {
        // Start loading state for plan update
        setIsUpdatingPlan(true);
        setSuccessMessage('Purchase successful! Updating your account...');
        
        const updateStartTime = Date.now();
        
        try {
          // Update profiles table immediately with new plan
          const { data: { user } } = await supabase.auth.getUser();
          if (user) {
            // Map product ID to plan type
            let planType = 'standard';
            let accountType = 'personal';
            
            if (productId.includes('pro')) {
              planType = 'pro';
              accountType = 'personal';
            } else if (productId.includes('team')) {
              planType = 'team';
              accountType = 'business';
            } else if (productId.includes('business')) {
              planType = 'business';
              accountType = 'business';
            }
            
            console.log('[IAP_COMPACT] Updating profiles table:', { planType, accountType });
            
            // Update profiles table
            const { error: profileError } = await supabase
              .from('profiles')
              .update({
                subscription_plan: planType,
                subscription_type: planType,
                account_type: accountType,
              })
              .eq('id', user.id);
            
            if (profileError) {
              console.error('[IAP_COMPACT] Failed to update profile:', profileError);
            } else {
              console.log('[IAP_COMPACT] ✅ Profile updated successfully');
            }
            
            // Update user metadata
            const { error: userError } = await supabase.auth.updateUser({
              data: {
                ...user.user_metadata,
                subscription_plan: planType,
                account_type: accountType,
              },
            });
            
            if (userError) {
              console.error('[IAP_COMPACT] Failed to update user metadata:', userError);
            }
          }
        } catch (updateError) {
          console.error('[IAP_COMPACT] Error updating account:', updateError);
        }
        
        // Ensure minimum 1.5s loading time for smooth UX
        const elapsedTime = Date.now() - updateStartTime;
        const remainingTime = Math.max(0, 1500 - elapsedTime);
        
        setTimeout(async () => {
          await loadSubscriptionData();
          setIsUpdatingPlan(false);
          setShowPlanModal(false);
          setSuccessMessage(null);
        }, remainingTime);
      } else {
        setError(result.error || 'Purchase failed. Please try again.');
      }
    } catch (err: any) {
      console.error('[IAP_COMPACT] Purchase error:', err);
      setError(err.message || 'An unexpected error occurred');
      setIsUpdatingPlan(false);
    } finally {
      setPurchasing(null);
    }
  };

  const handleManageSubscription = () => {
    window.open('https://apps.apple.com/account/subscriptions', '_blank');
  };

  if (checkingAccess) {
    return (
      <div className="bg-white rounded-xl p-6 border border-slate-200">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
      </div>
    );
  }

  if (!hasAccess) {
    return (
      <div className="bg-yellow-50 border-2 border-yellow-200 rounded-xl p-6">
        <div className="flex items-start gap-3">
          <AlertCircle className="text-yellow-600 flex-shrink-0 mt-0.5" size={20} />
          <div className="flex-1">
            <p className="font-semibold text-yellow-900">Subscription Management Restricted</p>
            <p className="text-sm text-yellow-800 mt-1">
              Only the organization owner (super admin) can manage subscriptions for business accounts.
            </p>
            <p className="text-sm text-yellow-700 mt-2">
              Please contact your organization owner to upgrade or change your subscription plan.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="space-y-4">
        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
            <AlertCircle className="text-red-600 flex-shrink-0" size={18} />
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        {/* Success Message */}
        {successMessage && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-start gap-2">
            <CheckCircle className="text-green-600 flex-shrink-0" size={18} />
            <p className="text-sm text-green-800">{successMessage}</p>
          </div>
        )}

        {/* Compact Current Plan Display */}
        {loading ? (
          <div className="bg-white rounded-xl p-6 border border-slate-200">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          </div>
        ) : currentSubscription?.isActive ? (
          <div className="bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-200 rounded-xl p-5">
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <CreditCard className="text-green-700" size={20} />
                  <h3 className="text-lg font-bold text-green-900">
                    {planDetails[currentSubscription.productId as keyof typeof planDetails]?.name || 'Active Plan'}
                  </h3>
                </div>
                {currentSubscription.expirationDate && (
                  <p className="text-sm text-green-700">
                    Renews {new Date(currentSubscription.expirationDate).toLocaleDateString('en-GB', {
                      day: 'numeric',
                      month: 'short',
                      year: 'numeric'
                    })}
                  </p>
                )}
              </div>
              <div className="px-3 py-1 bg-green-600 text-white rounded-full text-xs font-bold">
                ACTIVE
              </div>
            </div>

            <div className="flex gap-2">
              <button
                onClick={() => setShowPlanModal(true)}
                className="flex-1 px-4 py-2 bg-white hover:bg-gray-50 text-gray-700 rounded-lg font-medium text-sm transition-colors border border-gray-200"
              >
                Change Plan
              </button>
              <button
                onClick={handleManageSubscription}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium text-sm transition-colors"
              >
                <ExternalLink size={16} />
                Manage
              </button>
            </div>
          </div>
        ) : !Capacitor.isNativePlatform() ? (
          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-xl p-5">
            <div className="flex items-start gap-3 mb-3">
              <Smartphone className="text-blue-600 flex-shrink-0" size={24} />
              <div>
                <h3 className="text-lg font-bold text-blue-900 mb-1">iOS App Required</h3>
                <p className="text-sm text-blue-700">
                  Subscription management is only available on the iOS app due to Apple's In-App Purchase system.
                </p>
              </div>
            </div>
            <div className="bg-white rounded-lg p-3 border border-blue-200">
              <p className="text-xs text-gray-600">
                <strong className="text-gray-800">To manage your subscription:</strong><br/>
                1. Download the NoorCAD app from the App Store<br/>
                2. Sign in with this account<br/>
                3. Access Settings → Subscription Management
              </p>
            </div>
          </div>
        ) : currentUser?.user_metadata?.subscription_plan ? (
          // Fallback: Show subscription from user metadata if IAP not detecting it
          <div className="bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-300 rounded-xl p-5">
            <div className="flex items-start gap-3 mb-3">
              <CheckCircle className="text-green-600 flex-shrink-0" size={24} />
              <div>
                <h3 className="text-lg font-bold text-green-900 mb-1">
                  {currentUser.user_metadata.subscription_plan.charAt(0).toUpperCase() + 
                   currentUser.user_metadata.subscription_plan.slice(1)} Plan
                </h3>
                <p className="text-sm text-green-700">
                  Active subscription via Apple
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setShowPlanModal(true)}
                className="flex-1 px-4 py-2 bg-white hover:bg-green-50 text-green-700 rounded-lg font-medium text-sm transition-colors border-2 border-green-300"
              >
                Change Plan
              </button>
              <button
                onClick={handleManageSubscription}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium text-sm transition-colors"
              >
                <ExternalLink size={16} />
                Manage
              </button>
            </div>
          </div>
        ) : (
          <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-5">
            <div className="flex items-start gap-3 mb-3">
              <CreditCard className="text-blue-600 flex-shrink-0" size={20} />
              <div>
                <h3 className="text-lg font-bold text-blue-900 mb-1">No Active Subscription</h3>
                <p className="text-sm text-blue-700">
                  Subscribe to unlock all features and premium support
                </p>
              </div>
            </div>
            <button
              onClick={() => setShowPlanModal(true)}
              className="w-full px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold transition-colors"
            >
              View Plans
            </button>
          </div>
        )}
      </div>

      {/* Plan Selection Modal */}
      {showPlanModal && createPortal(
        <div className="fixed inset-0 bg-gradient-to-br from-slate-900/80 via-slate-800/60 to-slate-900/80 backdrop-blur-sm flex items-center justify-center p-4 z-[9999]">
          <div className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden border border-slate-200/50 relative">
            {/* Loading Overlay */}
            {isUpdatingPlan && (
              <div className="absolute inset-0 bg-white/95 backdrop-blur-md z-50 flex items-center justify-center rounded-3xl">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-16 w-16 border-4 border-indigo-200 border-t-indigo-600 mx-auto mb-4"></div>
                  <h3 className="text-xl font-bold text-slate-900 mb-2">Updating Your Plan</h3>
                  <p className="text-sm text-slate-600">Please wait while we activate your subscription...</p>
                </div>
              </div>
            )}
            
            {/* Modal Header */}
            <div className="relative bg-gradient-to-r from-indigo-600 via-purple-600 to-blue-600 px-6 py-5">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-white/20 rounded-xl backdrop-blur-sm">
                    <CreditCard className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white">Choose Your Plan</h3>
                    <p className="text-blue-100 text-sm">
                      {currentUser?.user_metadata?.account_type === 'business' ? 'Business Plans' : 'Available Plans'}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setShowPlanModal(false)}
                  className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-white" />
                </button>
              </div>
              <div className="absolute inset-0 bg-gradient-to-r from-indigo-600/0 via-white/5 to-blue-600/0 pointer-events-none"></div>
            </div>

            {/* Plans Grid */}
            <div className="p-5 grid grid-cols-2 gap-3">
              {Object.entries(planDetails)
                .filter(([productId]) => {
                  const accountType = currentUser?.user_metadata?.account_type;
                  
                  // Hide PRO_TRIAL unless it's the current plan
                  if (productId === PRODUCT_IDS.PRO_TRIAL && currentSubscription?.productId !== PRODUCT_IDS.PRO_TRIAL) {
                    return false;
                  }
                  
                  // Business accounts only see TEAM and BUSINESS plans
                  if (accountType === 'business') {
                    return productId === PRODUCT_IDS.TEAM || productId === PRODUCT_IDS.BUSINESS;
                  }
                  
                  // Personal accounts see all except TEAM_MEMBER_SLOT
                  return productId !== PRODUCT_IDS.TEAM_MEMBER_SLOT;
                })
                .map(([productId, details]) => {
                  const product = products.find(p => p.id === productId);
                  // Check both productId and subscription plan from user metadata
                  const userPlan = currentUser?.user_metadata?.subscription_plan?.toLowerCase();
                  // Improved plan matching: 'pro' matches PRO product ID, etc.
                  const isCurrentPlan = currentSubscription?.productId === productId || 
                                       (userPlan && (productId.toLowerCase().includes(userPlan) || (userPlan === 'pro_trial' && productId === PRODUCT_IDS.PRO_TRIAL)));
                  const Icon = details.icon;
                  
                  // Use hardcoded GBP pricing from planDetails, fallback to IAP price if available
                  const displayPrice = details.price !== undefined ? `£${details.price}` : (product?.price || '...');

                  return (
                    <div
                      key={productId}
                      className={`border-2 rounded-xl p-4 ${
                        isCurrentPlan
                          ? 'border-green-400 bg-gradient-to-br from-green-50 to-emerald-50'
                          : 'border-slate-200 hover:border-indigo-300 bg-white hover:shadow-md'
                      } transition-all`}
                    >
                      <div className="flex items-center gap-2 mb-3">
                        <Icon className={`text-${details.color}-600`} size={24} />
                        <h3 className="text-lg font-bold text-slate-900">{details.name}</h3>
                      </div>
                      
                      <p className="text-2xl font-bold text-indigo-600 mb-3">
                        {displayPrice}
                        <span className="text-xs font-normal text-slate-600">{details.price === 0 ? ' (free trial)' : '/mo'}</span>
                      </p>

                      <ul className="space-y-1.5 mb-4">
                        {details.features.slice(0, 3).map((feature: string, idx: number) => (
                          <li key={idx} className="flex items-start gap-1.5 text-xs text-slate-700">
                            <CheckCircle size={14} className="text-green-600 flex-shrink-0 mt-0.5" />
                            <span>{feature}</span>
                          </li>
                        ))}
                      </ul>

                      {!isCurrentPlan ? (
                        <button
                          onClick={() => handlePurchase(productId)}
                          disabled={purchasing !== null || !product}
                          className={`w-full px-3 py-2 rounded-lg font-semibold text-sm transition-all ${
                            purchasing === productId
                              ? 'bg-slate-400 text-white cursor-not-allowed'
                              : !product
                              ? 'bg-slate-300 text-slate-600 cursor-not-allowed'
                              : 'bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white shadow-md hover:shadow-lg'
                          }`}
                        >
                          {purchasing === productId ? 'Processing...' : loadingProducts ? 'Loading...' : product ? 'Subscribe' : 'Unavailable'}
                        </button>
                      ) : (
                        <button
                          disabled
                          className="w-full px-3 py-2 rounded-lg bg-slate-200 text-slate-500 text-center text-sm font-bold border-2 border-slate-300 cursor-not-allowed"
                        >
                          ✓ Current Plan
                        </button>
                      )}
                    </div>
                  );
                })}
            </div>

            {/* Subscription Information & Legal (Apple Guideline 3.1.2) */}
            <div className="px-5 pb-5 space-y-3">
              {/* Auto-Renewal Info */}
              <div className="bg-slate-50 rounded-lg p-3 border border-slate-200">
                <p className="text-xs text-slate-600 text-center">
                  💳 <strong>Auto-Renewable Subscription</strong><br/>
                  Payment charged to Apple ID at confirmation of purchase. Automatically renews unless cancelled at least 24 hours before the end of the current period.
                </p>
              </div>

              {/* Manage Subscription */}
              <div className="bg-blue-50 rounded-lg p-3 border border-blue-200">
                <p className="text-xs text-blue-800 text-center mb-2">
                  <strong>Manage Your Subscription</strong>
                </p>
                <a 
                  href="https://apps.apple.com/account/subscriptions" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="block text-center text-xs text-blue-600 hover:text-blue-800 underline font-medium"
                >
                  iOS Settings → [Your Name] → Subscriptions
                </a>
              </div>

              {/* Privacy Policy & Terms */}
              <div className="flex gap-2 justify-center">
                <a 
                  href="https://noorcad.com/privacy" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-xs text-slate-600 hover:text-slate-900 underline"
                >
                  Privacy Policy
                </a>
                <span className="text-xs text-slate-400">•</span>
                <a 
                  href="https://noorcad.com/terms" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-xs text-slate-600 hover:text-slate-900 underline"
                >
                  Terms of Use
                </a>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default SubscriptionManagementCompact;
